import { useState } from 'react';
import { Link } from 'react-router-dom';
import { VisitorPublicKioskForm } from './VisitorPublicKioskForm';
import { VisitorDataManagerTable } from './VisitorDataManagerTable';
import { VisitorAuditPrintView } from './VisitorAuditPrintView';

export function VisitorSystemModule() {
  const [activeTab, setActiveTab] = useState<'kiosk' | 'database' | 'audit'>('database');

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* 頂部導航與標題 (列印時隱藏) */}
      <div className="no-print flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <nav className="flex items-center space-x-2 text-xs text-slate-500 mb-1">
            <Link to="/frontline-lab" className="hover:text-emerald-700">
              現場工具箱
            </Link>
            <span>/</span>
            <span className="text-slate-800 font-bold">訪客與志工登記三件套</span>
          </nav>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-black text-slate-900">訪客與志工線上登記模組</h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
              三合一工作台
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <a
            href="/public/visitor"
            target="_blank"
            rel="noopener noreferrer"
            className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-lg border border-slate-300 flex items-center transition-colors"
          >
            <svg className="w-4 h-4 mr-1.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            另開獨立訪客全螢幕端 (Kiosk)
          </a>
        </div>
      </div>

      {/* 三合一 Tab 選項卡 (列印時隱藏) */}
      <div className="no-print flex space-x-1 bg-slate-200/80 p-1 rounded-xl max-w-xl">
        <button
          onClick={() => setActiveTab('kiosk')}
          className={`flex-1 min-h-[44px] py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center space-x-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
            activeTab === 'kiosk'
              ? 'bg-white text-emerald-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span>1. 訪客端手機填寫預覽</span>
        </button>

        <button
          onClick={() => setActiveTab('database')}
          className={`flex-1 min-h-[44px] py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center space-x-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
            activeTab === 'database'
              ? 'bg-white text-emerald-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7c0-2-1.5-3-3.5-3h-9C5.5 4 4 5 4 7z" />
          </svg>
          <span>2. 內部資料庫即時清單</span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex-1 min-h-[44px] py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all flex items-center justify-center space-x-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 ${
            activeTab === 'audit'
              ? 'bg-white text-emerald-800 shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          <span>3. A4 評鑑報表列印</span>
        </button>
      </div>

      {/* Tab 內容渲染 */}
      <div className="mt-4">
        {activeTab === 'kiosk' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 flex justify-center">
              {/* 行動端與現場機台模擬器 */}
              <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs py-2.5 px-4 flex justify-between items-center font-medium">
                  <span className="flex items-center space-x-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-600"></span>
                    <span className="font-semibold text-slate-800">大門現場填寫機 (390px 模擬視窗)</span>
                  </span>
                  <span className="text-slate-500 text-xs">掃碼即開 · 免登入</span>
                </div>
                <div className="p-1 sm:p-2 bg-slate-50/50">
                  <VisitorPublicKioskForm isEmbedded={true} />
                </div>
              </div>
            </div>

            {/* 右側資訊與操作指引 */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="text-base font-bold text-slate-800 flex items-center">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></span>
                現場部署與即時驗證指引
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                左側為訪客、志工於大門掃描 QR Code 或於公用平板所見之填寫畫面。
              </p>
              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-950 space-y-2.5">
                <div className="font-bold text-sm text-emerald-900">現場三步驗證法：</div>
                <div>1. 點擊右上角「另開獨立訪客全螢幕端」複製連結或產生 QR Code。</div>
                <div>2. 在左側直接試填一筆資料（例如體溫 36.8°C）。</div>
                <div>3. 切換至「2. 內部資料庫即時清單」確認資料已立刻寫入！</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'database' && <VisitorDataManagerTable />}

        {activeTab === 'audit' && <VisitorAuditPrintView />}
      </div>
    </div>
  );
}
